var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTerminologyTranslation } from '../../app/terminology';
import { Accordion, ConfirmDialog } from '../../common';
import { BasePanel } from '../common/basePanel';
import { LocationTable } from '../locationTable';
import { useQueueAPI } from '../queue/selectors';
import { QueueStatusType } from '../queue/types';
import { usePlanType, useQueueLocations } from '../selectors';
export var LearningCurveContent = function () {
    return (_jsx(LocationTable, { enableQuickInput: false, name: "learnCurve", min: 1, max: 100, suffix: "%", showGroupHeader: true, usePeriodLabels: true }));
};
export var useLearningCurveAttritionGuard = function () {
    var _a, _b, _c;
    var t = useTerminologyTranslation('wizard', { keyPrefix: 'learnCurve' }).t;
    var _d = useFormContext(), setValue = _d.setValue, getValues = _d.getValues;
    var queueData = useQueueAPI()[0];
    var formMode = (_a = useWatch({ name: 'attritionEntryMode' })) !== null && _a !== void 0 ? _a : 'percentage';
    var _e = useState(false), showDialog = _e[0], setShowDialog = _e[1];
    var _f = useState([]), pendingChanges = _f[0], setPendingChanges = _f[1];
    var prevLearnCurvesRef = useRef([]);
    var isAbsoluteMode = formMode === 'absolute';
    // Subscribe to learnCurve fields
    var volumeCount = (_c = (_b = getValues('volumes')) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
    var queueLocations = useQueueLocations();
    var hasQueueLocations = !!queueLocations.length;
    var learnCurveFieldNames = useMemo(function () {
        if (hasQueueLocations) {
            var names_1 = [];
            queueLocations.forEach(function (_, locIdx) {
                for (var i = 0; i < volumeCount; i++) {
                    names_1.push("queueLocations.".concat(locIdx, ".volumes.").concat(i, ".learnCurve"));
                }
            });
            return names_1;
        }
        return Array.from({ length: volumeCount }, function (_, i) { return "volumes.".concat(i, ".learnCurve"); });
    }, [hasQueueLocations, queueLocations, volumeCount]);
    var learnCurveValues = useWatch({
        name: learnCurveFieldNames,
        disabled: volumeCount === 0,
    });
    // Monitor learning curve changes
    useEffect(function () {
        var _a, _b, _c, _d;
        if (!isAbsoluteMode || !(learnCurveValues === null || learnCurveValues === void 0 ? void 0 : learnCurveValues.length)) {
            prevLearnCurvesRef.current = learnCurveValues !== null && learnCurveValues !== void 0 ? learnCurveValues : [];
            return;
        }
        var prevLearnCurves = prevLearnCurvesRef.current;
        if (prevLearnCurves.length !== learnCurveValues.length) {
            prevLearnCurvesRef.current = learnCurveValues;
            return;
        }
        // Check if any learning curve changed from 100 to <100
        var changes = [];
        for (var i = 0; i < learnCurveValues.length; i++) {
            var prevLC = prevLearnCurves[i];
            var newLC = learnCurveValues[i];
            if (prevLC === 100 && newLC != null && Number(newLC) < 100) {
                changes.push({ index: i, newValue: Number(newLC) });
            }
        }
        if (changes.length) {
            // Check the API/server data for attrition values because the form on
            // the HandleTime step does not include attrition fields after reset.
            var hasAttritionData = hasQueueLocations
                ? (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.queueLocations) === null || _b === void 0 ? void 0 : _b.some(function (loc) { var _a; return (_a = loc === null || loc === void 0 ? void 0 : loc.volumes) === null || _a === void 0 ? void 0 : _a.some(function (v) { return (v === null || v === void 0 ? void 0 : v.attrition) != null; }); })
                : (_d = (_c = queueData.data) === null || _c === void 0 ? void 0 : _c.volumes) === null || _d === void 0 ? void 0 : _d.some(function (v) { return (v === null || v === void 0 ? void 0 : v.attrition) != null; });
            if (hasAttritionData) {
                setPendingChanges(changes);
                setShowDialog(true);
                changes.forEach(function (ch) {
                    setValue(learnCurveFieldNames[ch.index], 100, { shouldDirty: false });
                });
                return;
            }
            // No attrition data, then silently reset to percentage mode and clear attrition
            var currentVolumes = getValues('volumes');
            currentVolumes === null || currentVolumes === void 0 ? void 0 : currentVolumes.forEach(function (_, idx) {
                setValue("volumes.".concat(idx, ".attrition"), null, { shouldDirty: true });
            });
            var currentQueueLocations = getValues('queueLocations');
            currentQueueLocations === null || currentQueueLocations === void 0 ? void 0 : currentQueueLocations.forEach(function (loc, locIdx) {
                var _a;
                (_a = loc === null || loc === void 0 ? void 0 : loc.volumes) === null || _a === void 0 ? void 0 : _a.forEach(function (_, idx) {
                    setValue("queueLocations.".concat(locIdx, ".volumes.").concat(idx, ".attrition"), null, { shouldDirty: true });
                });
            });
            setValue('attritionEntryMode', 'percentage', { shouldDirty: true });
        }
        prevLearnCurvesRef.current = __spreadArray([], learnCurveValues, true);
    }, [learnCurveValues, isAbsoluteMode, setValue, getValues, queueData.data, learnCurveFieldNames, hasQueueLocations]);
    var handleConfirm = useCallback(function () {
        if (pendingChanges.length > 0) {
            // Apply the learning curve changes
            pendingChanges.forEach(function (ch) {
                setValue(learnCurveFieldNames[ch.index], ch.newValue, { shouldDirty: true });
            });
            // Clear all attrition values
            var currentQueueLocations = getValues('queueLocations');
            currentQueueLocations === null || currentQueueLocations === void 0 ? void 0 : currentQueueLocations.forEach(function (loc, locIdx) {
                var _a;
                (_a = loc === null || loc === void 0 ? void 0 : loc.volumes) === null || _a === void 0 ? void 0 : _a.forEach(function (_, idx) {
                    setValue("queueLocations.".concat(locIdx, ".volumes.").concat(idx, ".attrition"), null, { shouldDirty: true });
                });
            });
            var currentVolumes = getValues('volumes');
            currentVolumes === null || currentVolumes === void 0 ? void 0 : currentVolumes.forEach(function (_, idx) {
                setValue("volumes.".concat(idx, ".attrition"), null, { shouldDirty: true });
            });
            setValue('attritionEntryMode', 'percentage', { shouldDirty: true });
        }
        setPendingChanges([]);
        setShowDialog(false);
        prevLearnCurvesRef.current = learnCurveFieldNames.map(function (name) { var _a; return (_a = getValues(name)) !== null && _a !== void 0 ? _a : null; });
    }, [pendingChanges, learnCurveFieldNames, setValue, getValues]);
    var handleCancel = useCallback(function () {
        setPendingChanges([]);
        setShowDialog(false);
    }, []);
    return { showDialog: showDialog, handleConfirm: handleConfirm, handleCancel: handleCancel, t: t };
};
export var LearningCurveAccordion = function () {
    var t = useTerminologyTranslation('wizard', { keyPrefix: 'learnCurve' }).t;
    var guard = useLearningCurveAttritionGuard();
    var planType = usePlanType();
    var queueLocations = useQueueLocations();
    var description = queueLocations.length ? t('locationDescription', { planType: planType }) : t('description', { planType: planType });
    return (_jsxs(_Fragment, { children: [_jsx(Accordion, __assign({ title: t('title'), defaultExpanded: true }, { children: _jsx(BasePanel, __assign({ description: description, statusType: QueueStatusType.LearnCurve, needSaveBeforeCopy: true }, { children: _jsx(LearningCurveContent, {}) })) })), _jsx(ConfirmDialog, { open: guard.showDialog, onClose: guard.handleCancel, title: guard.t('attritionModeChangeTitle'), message: guard.t('attritionModeChangeMessage'), onConfirm: guard.handleConfirm, confirmButtonText: "continue", cancelButtonText: "cancel" })] }));
};
